import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as videosApi from '../api/videos';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import { useOrg } from '../lib/org';
import { ErrorAlert, Spinner, StatusPill, SelectField, Field, TextareaField, formatDateTime } from '../components/ui';
import { ui, cn } from '../lib/ui';

const AUDIENCE_OPTIONS = ['public', 'registered', 'members', 'group'];

export default function VideoDetail() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { activeOrg } = useOrg();

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [watchUrl, setWatchUrl] = useState(null);
  const [watchError, setWatchError] = useState(null);
  const [watchLoading, setWatchLoading] = useState(false);

  const isOrgAdminOrMod = activeOrg && ['org-admin', 'org-moderator'].includes(activeOrg.role);

  const loadVideo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await videosApi.getVideo(videoId);
      setVideo(data.video || data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => { loadVideo(); }, [loadVideo]);

  function startEdit() {
    setForm({
      title: video.title,
      description: video.description || '',
      audienceLevel: video.audienceLevel,
    });
    setEditing(true);
  }

  async function saveEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await videosApi.updateVideo(videoId, form);
      setVideo(updated.video || updated);
      setEditing(false);
      toast.success('Video updated.');
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    const ok = await confirm('Delete this video? This cannot be undone.');
    if (!ok) return;
    try {
      await videosApi.deleteVideo(videoId);
      toast.success('Video deleted.');
      navigate('/videos');
    } catch (err) {
      toast.error(err);
    }
  }

  async function onWatch() {
    setWatchLoading(true);
    setWatchError(null);
    try {
      const data = await videosApi.watchVideo(videoId);
      setWatchUrl(data.watchUrl);
    } catch (err) {
      setWatchError(err);
    } finally {
      setWatchLoading(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorAlert error={error} />;
  if (!video) return <p>Video not found.</p>;

  return (
    <div>
      <button className={cn(ui.btnGhost, 'mb-4')} onClick={() => navigate('/videos')}>
        &larr; Back to videos
      </button>

      {video.status === 'ready' && (
        <div className="mb-6">
          {watchUrl ? (
            <video
              controls
              className="w-full max-w-200 rounded-dcon-md bg-dcon-deep"
              src={watchUrl}
            >
              Your browser does not support the video element.
            </video>
          ) : (
            <div className="max-w-200 rounded-dcon-md bg-dcon-deep p-12 text-center text-dcon-on-deep">
              <button className={ui.btnPrimary} onClick={onWatch} disabled={watchLoading}>
                {watchLoading ? 'Loading…' : 'Play video'}
              </button>
              {watchError && <ErrorAlert error={watchError} />}
            </div>
          )}
        </div>
      )}

      {video.status === 'processing' && (
        <div className="mb-6 rounded-dcon-md bg-z-sunken p-8 text-center">
          <p>This video is being processed. It will be available for playback once transcoding is complete.</p>
        </div>
      )}

      {editing ? (
        <form onSubmit={saveEdit}>
          <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <TextareaField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
          <SelectField label="Audience" value={form.audienceLevel} onChange={(e) => setForm({ ...form, audienceLevel: e.target.value })}>
            {AUDIENCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </SelectField>
          <div className="mt-4 flex gap-2">
            <button type="submit" className={ui.btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className={ui.btnGhost} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div>
          <h1 className={ui.h1}>{video.title}</h1>
          <div className="mb-4 flex flex-wrap items-center gap-4">
            <StatusPill status={video.status} />
            <span>Audience: {video.audienceLevel}</span>
            <span>Uploaded: {formatDateTime(video.createdAt)}</span>
          </div>
          {video.description && <p>{video.description}</p>}

          {isOrgAdminOrMod && (
            <div className="mt-4 flex gap-2">
              <button className={ui.btnGhost} onClick={startEdit}>Edit</button>
              {activeOrg.role === 'org-admin' && (
                <button className={ui.btnDanger} onClick={onDelete}>Delete</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
